import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { TrainingType } from 'src/app/domain/registration/enums/training-type.enum';
import { SystemTypeEnum } from 'src/app/domain/registration/enums/system-type.enum';
import { S3_URL_BASE } from 'src/environments/environment';
import { debounceTime, Subject } from 'rxjs';

@Component({
  selector: 'table-responsive-trainings',
  templateUrl: './table-responsive-trainings.component.html',
  styleUrls: ['./table-responsive-trainings.component.sass'],
})
export class TableResponsiveTrainingsComponent implements OnInit, OnChanges {
  @Input() dataSource: MatTableDataSource<any>;
  @Input() length: number = 0;
  @Input() hasAdd: boolean = true;
  @Input() currentTab: string = '';
  @Input() userType: string = '';
  @Input() loadingDownloads: { [key: string]: boolean } = {};
  @Input() loadingProgress: { [key: string]: number } = {};

  // Permissions
  @Input() isEditableShow: boolean = true;
  @Input() isEditableEnabled: boolean = true;
  @Input() isDeletableShow: boolean = true;
  @Input() isDeletableEnabled: boolean = true;
  @Input() isLockActionShow: boolean = true;
  @Input() isLockActionEnabled: boolean = true;

  // Show/Hide buttons
  @Input() showLock: boolean = false;
  @Input() showDownload: boolean = false;
  @Input() showDescription: boolean = false;
  @Input() showPlayButton: boolean = false;
  @Input() lockProperty: string = '';

  // Events
  @Output() changePage = new EventEmitter<any>();
  @Output() filter = new EventEmitter<any>();
  @Output() resetFilter = new EventEmitter<any>();
  @Output() openShow = new EventEmitter<any>();
  @Output() openNew = new EventEmitter<any>();
  @Output() openEdit = new EventEmitter<any>();
  @Output() openDelete = new EventEmitter<any>();
  @Output() lock = new EventEmitter<any>();
  @Output() unlock = new EventEmitter<any>();
  @Output() download = new EventEmitter<any>();
  @Output() cancelDownload = new EventEmitter<any>();
  @Output() description = new EventEmitter<any>();
  @Output() play = new EventEmitter<any>();

  // Filter properties
  filterValue: string = '';
  selectedTypeFilter: TrainingType | '' = '';
  selectedSystemTypeFilter: SystemTypeEnum | '' = '';
  
  // View mode
  viewMode: 'grid' | 'list' = 'grid';
  
  // Pagination
  pageSize = 12; // Tamanho da página padrão
  currentPage = 0; // Página atual
  
  // Enum references
  trainingTypes = Object.values(TrainingType);
  systemTypes = Object.values(SystemTypeEnum);
  
  // Internal data - Agora usa diretamente o dataSource
  filteredData: any[] = [];
  
  private filterSubject = new Subject<string>();

  ngOnInit(): void {
    console.log('TableResponsiveTrainingsComponent initialized, length:', this.length);
    this.setupFilter();
    this.updateFilteredData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSource']) {
      this.updateFilteredData();
    }
    if (changes['length']) {
      console.log('Length changed:', this.length);
    }
  }

  private setupFilter(): void {
    this.filterSubject.pipe(
      debounceTime(300)
    ).subscribe((value) => {
      this.filterValue = value;
      this.applyFilters();
    });
  }

  applySearch(value: string): void {
    this.filterSubject.next(value);
  }

  applyFilters(): void {
    // Emitir filtro para o componente pai lidar com a paginação
    const filterData = {
      searchTerm: this.filterValue,
      typeFilter: this.selectedTypeFilter,
      systemTypeFilter: this.selectedSystemTypeFilter
    };
    
    this.filter.emit(filterData);
  }

  private updateFilteredData(): void {
    // Usar diretamente os dados do dataSource
    this.filteredData = this.dataSource?.data || [];
    console.log('updateFilteredData called, filteredData length:', this.filteredData.length);
  }

  onPageChange(event: any): void {
    // Atualizar página atual
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    
    // Emitir evento de paginação para o componente pai
    this.changePage.emit({
      page: event.pageIndex,
      limit: event.pageSize
    });
  }

  getImageUrl(imagePath: string): string {
    if (!imagePath) return '';
    return imagePath.startsWith('http') ? imagePath : `${S3_URL_BASE}${imagePath}`;
  }

  getFileIcon(fileType: string): string {
    const extension = fileType?.toLowerCase();
    switch (extension) {
      case 'pdf': return 'picture_as_pdf';
      case 'mp4':
      case 'mkv':
      case 'avi': return 'play_circle_filled';
      case 'doc':
      case 'docx': return 'description';
      case 'xls':
      case 'xlsx': return 'table_chart';
      case 'ppt':
      case 'pptx': return 'slideshow';
      default: return 'attachment';
    }
  }

  clearFilters(): void {
    this.filterValue = '';
    this.selectedTypeFilter = '';
    this.selectedSystemTypeFilter = '';
    
    // Emitir evento de reset para o componente pai
    this.resetFilter.emit();
    
    // Aplicar filtros vazios
    this.applyFilters();
  }

  toggleViewMode(): void {
    this.viewMode = this.viewMode === 'grid' ? 'list' : 'grid';
  }

  // Action methods
  onShow(element: any): void {
    this.openShow.emit(element);
  }

  onEdit(element: any): void {
    this.openEdit.emit(element);
  }

  onDelete(element: any): void {
    this.openDelete.emit(element);
  }

  onLock(element: any): void {
    if (!this.lockProperty) {
      return;
    }
    
    if (element[this.lockProperty]) {
      this.unlock.emit(element);
    } else {
      this.lock.emit(element);
    }
  }

  onDownload(element: any): void {
    this.download.emit({ fileUrl: element.fileAttachment, element });
  }

  onCancelDownload(element: any): void {
    this.cancelDownload.emit(element);
  }

  onDescription(element: any): void {
    this.description.emit(element);
  }

  onPlay(element: any): void {
    this.play.emit(element);
  }

  onNew(): void {
    this.openNew.emit({ userType: this.userType });
  }

  formatFileSize(size: string): string {
    return size || 'N/A';
  }

  formatDate(date: string): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('pt-BR');
  }

  getProgressPercentage(elementId: string): number {
    return this.loadingProgress[elementId] || 0;
  }

  isDownloading(elementId: string): boolean {
    return this.loadingDownloads[elementId] || false;
  }

  shouldShowPagination(): boolean {
    return this.length > 0;
  }

  getPageSizeOptions(): number[] {
    return [6, 12, 24, 48, 100];
  }

  isPaginationStable(): boolean {
    return this.shouldShowPagination();
  }
} 